import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { BEST_ENDEAVOURS_NOTE, PREFERENCES_PRIVACY_HINT } from '../../lib/copy';
import './matchday.css';

interface OutstandingRow {
  teamId: number;
  teamName: string;
  matchdayDate: string; // yyyy-MM-dd
  matchdayLabel: string | null;
}

const MATCH_TYPES = ['League', 'Cup', 'Friendly'] as const;
type MatchTypeName = (typeof MATCH_TYPES)[number];

/**
 * Keyed by user id so one coach's "not now" never silences another account in the same tab.
 * Exported so the payment prompt can queue behind this one rather than stacking on top of it.
 */
export const DISMISS_KEY = 'matchday-prompt-dismissed';

/** Query key shared with the payment prompt, which waits for this check-in to be dealt with. */
export const MATCHDAY_OUTSTANDING_KEY = ['matchday', 'outstanding'];

function fmtMatchday(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * After-login matchday check-in. Shows once per session when the signed-in user coaches teams
 * with an unanswered reminder for an upcoming matchday: yes/no per team, with the game type and
 * any preferences captured alongside a "yes". Backed by /api/matchday/mine — the session is the
 * credential, unlike the emailed token links, and answers land in the same working list.
 *
 * The payment prompt queues behind this one, so dismissal is a cross-component contract:
 * dismiss() must call onDismiss (the shell's reactive signal) as well as writing sessionStorage,
 * or the payment prompt stays hidden until the next navigation.
 */
export function MatchdayPrompt({ onDismiss }: { onDismiss?: () => void }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);

  const enabled = !!user && user.status === 'Active';
  const outstanding = useQuery({
    queryKey: MATCHDAY_OUTSTANDING_KEY,
    queryFn: () => api<OutstandingRow[]>('/api/matchday/mine/outstanding'),
    enabled,
  });

  const respond = useMutation({
    mutationFn: (body: {
      teamId: number;
      date: string;
      needsPitch: boolean;
      matchType: MatchTypeName | null;
      preferences: string | null;
    }) => api('/api/matchday/mine/confirm', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (_data, v) => {
      toast(
        v.needsPitch
          ? 'Thanks — pitch requested, the club will allocate one'
          : 'Thanks — recorded as no pitch needed',
        'success',
      );
      void queryClient.invalidateQueries({ queryKey: ['matchday', 'outstanding'] });
    },
    onError: (err) =>
      toast(err instanceof ApiError ? err.message : 'Something went wrong — try again', 'error'),
  });

  if (!enabled || dismissed) return null;
  if (sessionStorage.getItem(DISMISS_KEY) === String(user.userId)) return null;

  const rows = outstanding.data ?? [];
  if (rows.length === 0) return null;

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, String(user!.userId));
    setDismissed(true);
    // sessionStorage writes re-render nothing — this tells the shell so the payment prompt
    // queued behind this one can appear now rather than on the next navigation.
    onDismiss?.();
  }

  return (
    <div className="ui-dialog-backdrop" role="presentation" onClick={dismiss}>
      <div
        className="ui-dialog mday-prompt"
        role="dialog"
        aria-modal="true"
        aria-label="Matchday pitch check-in"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="ui-dialog-title">Do you need a pitch?</h2>
        <p className="mday-prompt-intro">
          {rows.length === 1
            ? 'You haven’t answered this matchday reminder yet:'
            : 'You haven’t answered these matchday reminders yet:'}
        </p>
        {rows.map((row) => (
          <PromptRow
            key={`${row.teamId}-${row.matchdayDate}`}
            row={row}
            busy={respond.isPending}
            onAnswer={(needsPitch, matchType, preferences) =>
              respond.mutate({
                teamId: row.teamId,
                date: row.matchdayDate,
                needsPitch,
                matchType,
                preferences,
              })
            }
          />
        ))}
        <div className="ui-dialog-actions">
          <Button variant="ghost" onClick={dismiss} disabled={respond.isPending}>
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}

function PromptRow({
  row,
  busy,
  onAnswer,
}: {
  row: OutstandingRow;
  busy: boolean;
  onAnswer: (needsPitch: boolean, matchType: MatchTypeName | null, preferences: string | null) => void;
}) {
  const [saidYes, setSaidYes] = useState(false);
  const [matchType, setMatchType] = useState<MatchTypeName>('League');
  const [preferences, setPreferences] = useState('');

  return (
    <div className="mday-prompt-row">
      <div className="mday-prompt-fixture">
        <strong>{row.teamName}</strong>
        <span>
          {fmtMatchday(row.matchdayDate)}
          {row.matchdayLabel ? ` — ${row.matchdayLabel}` : ''}
        </span>
      </div>
      {!saidYes ? (
        <div className="mday-prompt-answers">
          <Button size="sm" disabled={busy} onClick={() => setSaidYes(true)}>
            Yes, we need a pitch
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => onAnswer(false, null, null)}
          >
            No pitch needed
          </Button>
        </div>
      ) : (
        <div className="mday-prompt-details">
          <label className="mday-prompt-field">
            <span className="ui-label">Type of game</span>
            <select
              className="ui-select"
              value={matchType}
              onChange={(e) => setMatchType(e.target.value as MatchTypeName)}
            >
              {MATCH_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="mday-prompt-field">
            <span className="ui-label">Any preferences? (optional)</span>
            <textarea
              className="mday-prompt-notes"
              rows={2}
              maxLength={500}
              placeholder="e.g. early kick-off, happy to share a pitch…"
              value={preferences}
              onChange={(e) => setPreferences(e.target.value)}
            />
            <span className="mday-prompt-hint">{PREFERENCES_PRIVACY_HINT}</span>
            <span className="mday-prompt-hint">{BEST_ENDEAVOURS_NOTE}</span>
          </label>
          <div className="mday-prompt-answers">
            <Button
              size="sm"
              loading={busy}
              onClick={() => onAnswer(true, matchType, preferences.trim() || null)}
            >
              Confirm — pitch needed
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setSaidYes(false)}>
              Back
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
